import type {
  ServiceCenterAgent,
  ServiceCenterBenefit,
} from '@/api/directus/serviceCenterAgents';
import type {TravelType} from '@/modules/common/context/QueryParams/models';
import type {Page} from '@/types';
import type {Breakpoint} from '@travel-frontend/themes';

import Cookies from 'js-cookie';

import {PAGE_HOTELS} from '@/consts';

export const AGENT_STORAGE_KEY = 'serviceCenterAgent';
export const BENEFITS_STORAGE_KEY = 'serviceCenterBenefits';
export const AGENT_ID_STORAGE_KEY = 'serviceCenterBannerAgentId';

export const SC_BANNER_TREATMENT_A = 'agentPicture';
export const SC_BANNER_TREATMENT_B = 'enhancedAgentPicture';

export const isBannerEnabled = (
  page: Page,
  treatment: string,
  config?: {
    hotellist: {enabled: boolean};
    offerlist: {enabled: boolean};
  }
) =>
  config !== undefined &&
  (page === PAGE_HOTELS
    ? config.hotellist.enabled === true
    : config.offerlist.enabled === true) &&
  (treatment === SC_BANNER_TREATMENT_A || treatment === SC_BANNER_TREATMENT_B);

export const readfromCache = <Data>(storageKey: string): Data | undefined => {
  // no cookie found
  // so session probably expired
  // return undefined to knwo that we need to fetch a new random agent
  const idFromCookie = Cookies.get(AGENT_ID_STORAGE_KEY);
  if (!idFromCookie) {
    return undefined;
  }

  // else we try reading cache from localStorage
  try {
    const dataFromStorage = localStorage.getItem(storageKey);
    const parsedData = dataFromStorage
      ? JSON.parse(dataFromStorage)
      : undefined;

    if (
      storageKey === AGENT_ID_STORAGE_KEY &&
      idFromCookie !== parsedData?.id
    ) {
      // someone changed the ID in the cookie?
      // maybe fro testing all available agents
      // return undefined to knwo that we need to fetch a new random agent
      return undefined;
    }

    // this is only expecting the correct data is returned but not really safe
    // to make sure we get the expected data format we would need something like zod
    return parsedData as Data;
  } catch {
    // noop
  }

  // if something fails when reading from localStorage
  // return undefined to know that we need to fetch a new random agent
  return undefined;
};

export const getInitialState = () => ({
  agent: readfromCache<ServiceCenterAgent>(AGENT_STORAGE_KEY),
  benefits:
    readfromCache<Array<ServiceCenterBenefit>>(BENEFITS_STORAGE_KEY) ?? [],
});

export const getDevice = (
  currentBreakpoint: Breakpoint,
  isDesktop: boolean
) => {
  let device: 'sm' | 'md' | 'lg' = 'sm';
  if (isDesktop) {
    device = 'lg';
  } else if (currentBreakpoint === 'md') {
    device = 'md';
  }
  return device;
};

export const getHotline = ({
  device,
  page,
  travelType,
  agent,
}: {
  travelType: TravelType;
  page: Page;
  device: ReturnType<typeof getDevice>;
  agent?: ServiceCenterAgent;
}) => {
  return (
    agent?.phoneNumbers.find(
      (number) =>
        number.pageType === page &&
        number.devices.includes(device) &&
        number.travelTypes.includes(travelType)
    )?.number ?? ''
  );
};
